/**
 * After-Visit Summary (AVS) service.
 *
 * Generates a patient-facing visit summary at encounter close, lets the
 * provider edit instructions before publishing, publishes to the patient
 * portal (token-gated link, mirroring the prescription-link pattern), and
 * supports a print/download PDF fallback.
 *
 * Backend contract is intentionally aligned with the existing FHIR-shaped
 * encounter routes: `/emr/:clinicId/encounters/:id/avs/...`.
 */

import api from './api';

export type AvsStatus = 'draft' | 'published' | 'sent' | 'acknowledged';

export type AvsAiField = 'visit_summary' | 'patient_instructions' | 'diagnosis_explanations';

export interface AvsMedicationDiff {
  action: 'started' | 'stopped' | 'changed' | 'continued';
  medication_display: string;
  dose_text?: string | null;
  reason?: string | null;
}

export interface AvsOrderEntry {
  category: 'lab' | 'imaging' | 'referral' | 'procedure';
  display: string;
  patient_instructions?: string | null;
}

export interface AvsDiagnosisEntry {
  display: string;
  code?: string | null;
  category?: 'encounter-diagnosis' | 'problem-list-item' | null;
}

export interface AvsDiagnosisExplanation {
  code?: string | null;
  display: string;
  plain_language: string;
}

export interface AvsAiProvenanceStamp {
  generated_at: string;
  model_id: string;
}

export interface AfterVisitSummary {
  id: string;
  encounter_id: string;
  patient_id: string;
  clinic_id: string;
  practitioner_id: string;
  status: AvsStatus;
  chief_complaint?: string | null;
  visit_date: string;
  visit_summary?: string | null;
  diagnoses?: AvsDiagnosisEntry[];
  medication_changes?: AvsMedicationDiff[];
  orders?: AvsOrderEntry[];
  follow_up_date?: string | null;
  follow_up_notes?: string | null;
  patient_instructions?: string | null;
  diagnosis_explanations?: AvsDiagnosisExplanation[] | null;
  ai_provenance?: Partial<Record<AvsAiField, AvsAiProvenanceStamp>> | null;
  ai_token_usage?: { input_tokens?: number | null; output_tokens?: number | null; latency_ms?: number | null } | null;
  rendered_html?: string | null;
  pdf_url?: string | null;
  portal_token?: string | null;
  published_at?: string | null;
  sent_at?: string | null;
  acknowledged_at?: string | null;
  // Sequelize emits these as camelCase even with `underscored: true` because
  // they are auto-injected timestamp attributes; everything else on the row
  // stays snake_case.
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateAvsPayload {
  chief_complaint?: string;
  visit_summary?: string;
  patient_instructions?: string;
  diagnosis_explanations?: AvsDiagnosisExplanation[] | null;
  follow_up_date?: string | null;
  follow_up_notes?: string | null;
}

export interface PublishAvsOptions {
  email?: string;
  sendEmail?: boolean;
}

export const avsService = {
  /** Create / regenerate the AVS draft from the signed encounter content. */
  async generateAvs(clinicId: string, encounterId: string): Promise<AfterVisitSummary> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/avs/generate`,
    );
    return response.data?.data || response.data;
  },

  /** Fetch the current AVS (draft or published) for an encounter. */
  async getAvs(clinicId: string, encounterId: string): Promise<AfterVisitSummary | null> {
    try {
      const response = await api.get(`/emr/${clinicId}/encounters/${encounterId}/avs`);
      return response.data?.data || response.data || null;
    } catch (error: any) {
      // 404 → never generated yet; treat as null.
      if (error?.response?.status === 404) return null;
      throw error;
    }
  },

  /** Edit patient-facing fields on the draft before publishing. */
  async updateAvs(
    clinicId: string,
    encounterId: string,
    body: UpdateAvsPayload,
  ): Promise<AfterVisitSummary> {
    const response = await api.put(
      `/emr/${clinicId}/encounters/${encounterId}/avs`,
      body,
    );
    return response.data?.data || response.data;
  },

  /** Publish + email a portal-token link to the patient. */
  async publishAvs(
    clinicId: string,
    encounterId: string,
    options?: PublishAvsOptions,
  ): Promise<AfterVisitSummary> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/avs/publish`,
      options ?? {},
    );
    return response.data?.data || response.data;
  },

  /** Fetch the rendered PDF as a blob for print/download. */
  async downloadAvsPdf(clinicId: string, encounterId: string): Promise<Blob> {
    const response = await api.get(
      `/emr/${clinicId}/encounters/${encounterId}/avs.pdf`,
      { responseType: 'blob' } as any,
    );
    return response.data;
  },

  /**
   * Patient-portal read by token (mirrors `getPrescriptionLink`).
   * Used by the patient-facing AVS tab — no auth header required, the
   * backend validates the token.
   */
  async getAvsByToken(token: string): Promise<AfterVisitSummary> {
    const response = await api.get(`/clinic/storefront/avs/${token}`);
    return response.data?.data || response.data;
  },

  /** Acknowledge receipt from the patient portal (audit trail). */
  async acknowledgeAvs(token: string): Promise<{ acknowledged_at: string }> {
    const response = await api.post(`/clinic/storefront/avs/${token}/acknowledge`);
    return response.data?.data || response.data;
  },

  /** List published AVS entries for a patient (for the patient-portal list view). */
  async getPatientAvsList(
    clinicId: string,
    patientId: string,
  ): Promise<AfterVisitSummary[]> {
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/avs`,
    );
    return response.data?.data || response.data || [];
  },

  /** Discard a single AI-generated field on the AVS draft. */
  async discardAiField(
    clinicId: string,
    encounterId: string,
    field: AvsAiField,
  ): Promise<AfterVisitSummary> {
    const response = await api.delete(
      `/emr/${clinicId}/encounters/${encounterId}/avs/ai/${field}`,
    );
    return response.data?.data || response.data;
  },
};

export default avsService;
